import React from 'react';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Coins, Users, WarningCircle } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import { IAgingFaixa, useFinanceiroInadimplencia } from '../../../../hooks/useFinanceiro';
import { AnimatedBar } from '../components/AnimatedBar';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl, fmtData } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const diasAtraso = (iso: string | null) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const venc = new Date(y, (m || 1) - 1, d || 1);
  const hoje = new Date();
  const ms = hoje.setHours(0, 0, 0, 0) - venc.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor(ms / 86400000));
};

const FAIXA_LABEL: Record<string, string> = {
  A_VENCER: 'A vencer',
  D_1_30: '1–30 dias',
  D_31_60: '31–60 dias',
  D_61_90: '61–90 dias',
  D_90_MAIS: '+90 dias',
};

const AgingBar = ({ faixa, max }: { faixa: IAgingFaixa; max: number }) => {
  const pct = max > 0 && faixa.valor > 0 ? Math.max(4, (faixa.valor / max) * 100) : 0;
  const vencido = faixa.faixa !== 'A_VENCER';
  return (
    <Box>
      <Flex justify="space-between" fontSize="xs" color="#71717A" mb={1}>
        <Text>
          {FAIXA_LABEL[faixa.faixa] ?? faixa.faixa} · {faixa.qtd}
        </Text>
        <Text fontWeight={600} color="#0F172A">
          {brl(faixa.valor)}
        </Text>
      </Flex>
      <AnimatedBar pct={pct} cor={vencido ? '#D97706' : '#1B88F5'} h="8px" />
    </Box>
  );
};

export const FinanceiroInadimplenciaPage = () => {
  const f = useFinanceiroFilters();
  const { data, isError } = useFinanceiroInadimplencia({ base: 'vencimento', ...f.apiParams });

  // Aging só das faixas VENCIDAS (exclui "A vencer") — pra o card "Por faixa de
  // atraso" somar exatamente o "Total vencido".
  const agingVencido = (data?.aging ?? []).filter(af => af.faixa !== 'A_VENCER');
  const agingMax = Math.max(1, ...agingVencido.map(fx => fx.valor));
  const totalVencido = data?.total ?? 0;

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Recebíveis"
          titulo="Inadimplência de honorários"
          subtitulo="Recebíveis vencidos por cliente e por faixa de atraso"
          icon={<WarningCircle size={24} weight="fill" />}
          cor="#D97706"
          info={
            <Text>
              Honorários vencidos (não pagos) no período. O <b>aging</b> agrupa por faixa de atraso;
              a lista mostra dias de atraso e o <b>% do total</b> por cliente.
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar a inadimplência.</Text>
          </GlassCard>
        )}

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <KpiCard
            label="Total vencido"
            valor={totalVencido}
            qtd={data?.qtd}
            color="#D97706"
            icon={<WarningCircle size={20} weight="duotone" />}
            index={0}
            info={
              <InfoPopover title="Total vencido">
                Saldo não pago (<i>nao_pago</i>) de recebíveis <b>vencidos até o fim do período</b>{' '}
                selecionado. O início do período não corta — o atrasado antigo nunca some. No mês
                corrente equivale ao saldo de hoje. A quantidade é o nº de títulos vencidos.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Clientes"
            valor={data?.qtd_clientes ?? 0}
            format="number"
            color="#1B88F5"
            icon={<Users size={20} weight="duotone" />}
            index={1}
            info={
              <InfoPopover title="Clientes inadimplentes">
                Quantos clientes distintos têm pelo menos um honorário vencido em aberto no período.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Ticket médio"
            valor={totalVencido / Math.max(1, data?.qtd_clientes ?? 0)}
            color="#DC2626"
            icon={<Coins size={20} weight="duotone" />}
            index={2}
            info={
              <InfoPopover title="Ticket médio">
                <b>Total vencido ÷ nº de clientes inadimplentes</b> — quanto, em média, cada devedor
                representa.
              </InfoPopover>
            }
          />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
          {/* Aging */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <GlassCard intensity="strong" p={6} h="full">
              <Text fontWeight={700} color="#0F172A" mb={4}>
                Por faixa de atraso
              </Text>
              <Flex direction="column" gap={3}>
                {agingVencido.map(af => (
                  <AgingBar key={af.faixa} faixa={af} max={agingMax} />
                ))}
                {agingVencido.length === 0 && (
                  <Text fontSize="sm" color="#94A3B8">
                    Sem recebíveis vencidos.
                  </Text>
                )}
              </Flex>
            </GlassCard>
          </motion.div>

          {/* Clientes */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.05 }}
          >
            <GlassCard intensity="medium" p={6} h="full">
              <Text fontWeight={700} color="#0F172A" mb={4}>
                Clientes inadimplentes
              </Text>
              <Flex direction="column" gap={3}>
                {(data?.clientes ?? []).map(c => {
                  const dias = diasAtraso(c.data_vencimento);
                  return (
                    <Flex
                      key={`cliente-${c.pessoa_nome}`}
                      justify="space-between"
                      align="center"
                      gap={2}
                    >
                      <Box minW={0}>
                        <Text fontSize="sm" fontWeight={600} color="#0F172A" noOfLines={1}>
                          {c.pessoa_nome || '—'}
                        </Text>
                        <Text fontSize="xs" color="#94A3B8">
                          desde {fmtData(c.data_vencimento)}
                          {dias !== null ? ` · ${dias} dias` : ''}
                        </Text>
                      </Box>
                      <Box textAlign="right" whiteSpace="nowrap">
                        <Text fontSize="sm" fontWeight={700} color="#DC2626">
                          {brl(c.nao_pago)}
                        </Text>
                        <Text fontSize="11px" color="#94A3B8">
                          {totalVencido > 0
                            ? `${((c.nao_pago / totalVencido) * 100).toFixed(1).replace('.', ',')}%`
                            : '—'}
                        </Text>
                      </Box>
                    </Flex>
                  );
                })}
                {(data?.clientes?.length ?? 0) === 0 && (
                  <Text fontSize="sm" color="#94A3B8">
                    Nenhum cliente inadimplente.
                  </Text>
                )}
              </Flex>
            </GlassCard>
          </motion.div>
        </SimpleGrid>
      </Box>
    </GlassPageBg>
  );
};
